/**
 * Schema.org Structured Data Generator
 * Generates JSON-LD structured data for SEO
 */

/**
 * Generate Product Schema
 * https://schema.org/Product
 */
export const generateProductSchema = (product) => {
  if (!product) return null;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name || product.title,
    description: product.description,
    image: product.images?.map(img => 
      img.startsWith('http') ? img : `https://minelakids.com${img}`
    ) || [],
    sku: product.sku || product.id,
    brand: {
      '@type': 'Brand',
      name: 'Minela Kids'
    },
    offers: {
      '@type': 'Offer',
      url: `https://minelakids.com/products/${product.id}`,
      priceCurrency: 'TRY',
      price: product.price,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
      availability: product.stock > 0 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: 'Minela Kids'
      }
    }
  };

  // Add aggregate rating if reviews exist
  if (product.rating && product.reviewCount) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount,
      bestRating: 5,
      worstRating: 1
    };
  }

  // Add category
  if (product.category) {
    schema.category = product.category;
  }

  // Add color if available
  if (product.color) {
    schema.color = product.color;
  }

  // Add size if available
  if (product.size) {
    schema.size = product.size;
  }

  return schema;
};

/**
 * Generate BreadcrumbList Schema
 * https://schema.org/BreadcrumbList
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name || crumb.label,
      item: crumb.path?.startsWith('http') 
        ? crumb.path 
        : `https://minelakids.com${crumb.path}`
    }))
  };
};

/**
 * Generate Organization Schema
 * https://schema.org/Organization
 */
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Organization',
    name: 'Minela Kids',
    url: 'https://minelakids.com',
    logo: 'https://minelakids.com/minela_kids_logo.jpg',
    description: 'Kaliteli, şık ve konforlu çocuk giyim koleksiyonları. Bebekler ve çocuklar için özel tasarım kıyafetler.',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+90-XXX-XXX-XXXX', // Gerçek telefon numaranızı ekleyin
      contactType: 'customer service',
      availableLanguage: ['Turkish']
    },
    sameAs: [
      // Sosyal medya hesaplarınızı ekleyin
      // 'https://www.facebook.com/minelakids',
      // 'https://www.instagram.com/minelakids',
      // 'https://twitter.com/minelakids'
    ]
  };
};

/**
 * Generate WebSite Schema with SearchAction
 * https://schema.org/WebSite
 */
export const generateWebSiteSchema = () => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'WebSite',
    name: 'Minela Kids',
    url: 'https://minelakids.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://minelakids.com/products?search={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };
};

/**
 * Generate ItemList Schema (for product listings)
 * https://schema.org/ItemList
 */
export const generateItemListSchema = (products, listName = 'Products') => {
  if (!products || products.length === 0) return null;

  return {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://minelakids.com/products/${product.id}`,
      name: product.name || product.title,
      image: product.images?.[0]
    }))
  };
};

/**
 * Serialize schema to JSON-LD string
 */
export const serializeSchema = (schema) => {
  if (!schema) return '';
  return JSON.stringify(schema, null, 2);
};

/**
 * Combine multiple schemas
 */
export const combineSchemas = (schemas) => {
  const validSchemas = schemas.filter(Boolean);
  if (validSchemas.length === 0) return null;
  if (validSchemas.length === 1) return validSchemas[0];
  
  return {
    '@context': 'https://schema.org/',
    '@graph': validSchemas
  };
};

/**
 * Generate FAQPage Schema
 * https://schema.org/FAQPage
 */
export const generateFAQSchema = (faqItems) => {
  if (!faqItems || faqItems.length === 0) return null;

  return {
    '@context': 'https://schema.org/',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };
};

/**
 * Generate VideoObject Schema
 * https://schema.org/VideoObject
 */
export const generateVideoSchema = (video) => {
  if (!video) return null;

  return {
    '@context': 'https://schema.org/',
    '@type': 'VideoObject',
    name: video.name || video.title,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
    duration: video.duration, // ISO 8601 format: PT#M#S
    contentUrl: video.contentUrl,
    embedUrl: video.embedUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Minela Kids',
      logo: {
        '@type': 'ImageObject',
        url: 'https://minelakids.com/minela_kids_logo.jpg'
      }
    }
  };
};

/**
 * Generate Review Schema (for products)
 * https://schema.org/Review
 */
export const generateReviewSchema = (reviews) => {
  if (!reviews || reviews.length === 0) return null;

  return reviews.map(review => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.authorName
    },
    datePublished: review.datePublished,
    reviewBody: review.reviewBody,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1
    }
  }));
};
